import type { IFrameComponent, IframeResizerProps } from 'iframe-resizer-react';
import IframeResizer from 'iframe-resizer-react';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

import { Size } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';
import type { EventId } from '@swf/generated/types/api/sports';
import { EventTypeId } from '@swf/sports/types/eventType';

import { useSportsEventInfoWidget } from '../../../../widgets/sports-event-info-widget/model';

import styles from './DataVis.scss';
import LoadingState from './components/LoadingState';

interface DataVisProps extends IframeResizerProps {
  eventId: EventId;
  eventTypeId: EventTypeId;
  name: string;
}

interface GetIFrameSrcProps {
  origin: string;
  locale: string;
  eventId: number;
}

const getIframeSrc = ({ origin, locale, eventId }: GetIFrameSrcProps) =>
  `${origin}/sports/web/gbp/bvp/?tr=10&contentType=viz&allowPopup=true&width=2040&locale=${locale}&eID=${eventId}`;

export const DataVis = ({ eventId, name, eventTypeId, ...rest }: DataVisProps) => {
  const { widgetSelect } = useSportsEventInfoWidget();
  // if the sport is not Football or Tennis we need to account for additional scoreboard height
  // otherwise account for tabs only
  const isScoreboardsDisplayed =
    eventTypeId !== EventTypeId.SOCCER && eventTypeId !== EventTypeId.TENNIS;
  const additionalContentHeight = isScoreboardsDisplayed ? 137 : 40;
  const { greaterThan } = useScreenSize();
  const initialHeight = (greaterThan(Size.XSmall) ? 352 : 236) + additionalContentHeight;
  const isDataVisAvailable = useSelector(widgetSelect.isDataVisAvailable);
  const origin = useSelector(select.location.getOrigin);
  const locale = useSelector(select.locale.get);
  const initialised = useRef(false);

  if (!isDataVisAvailable) return null;

  const onResized = ({ iframe, height }: { iframe: IFrameComponent; height: number }) => {
    if (initialised.current) return;

    if (height > 0) {
      // wait until height property has been added to iframe - prevents small height adjustment/flicker
      setTimeout(() => iframe.style.removeProperty('min-height'), 500);
      initialised.current = true;
    }
  };

  return canUseDOM ? (
    <IframeResizer
      {...rest}
      onResized={onResized}
      className={styles.dataVisIframe}
      title={name}
      src={getIframeSrc({ origin, locale, eventId })}
      style={{ minHeight: initialised.current ? 0 : initialHeight }}
    />
  ) : (
    <LoadingState height={initialHeight} />
  );
};
